import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Назначение встречи: время, место, привязка и участники.
// Участников сколько угодно; каждый — сотрудник, контакт или внешний гость по email.
import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, Form, Input, Modal, Select } from 'antd';

import { useContacts } from '../../contacts/hooks/useContacts.js';
import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import { EntityLinkFields } from '../../entity-links/EntityLinkFields.js';
import { useEntityLinkCascade } from '../../entity-links/useEntityLinkCascade.js';
import { isoToLocalInput, localInputToIso, nowLocalInput } from '../../tasks/lib/taskMeta.js';
import type { CreateMeetingInput, MeetingItem, ParticipantInput } from '../api/meetings.client.js';
import { useCreateMeetingMutation, useUpdateMeetingMutation } from '../hooks/useMeetings.js';

/** Значения мультиселекта участников кодируем префиксом источника. */
const MEMBER_PREFIX = 'member:';
const CONTACT_PREFIX = 'contact:';

function participantsToValues(meeting: MeetingItem | null | undefined): string[] {
  if (!meeting) return [];
  return meeting.participants.map((participant) => {
    if (participant.memberId) return `${MEMBER_PREFIX}${participant.memberId}`;
    if (participant.contactId) return `${CONTACT_PREFIX}${participant.contactId}`;
    return participant.guestEmail ?? '';
  });
}

function valuesToParticipants(values: string[]): ParticipantInput[] {
  return values.map((value) => {
    if (value.startsWith(MEMBER_PREFIX)) return { memberId: value.slice(MEMBER_PREFIX.length) };
    if (value.startsWith(CONTACT_PREFIX)) return { contactId: value.slice(CONTACT_PREFIX.length) };
    // Всё остальное — введённый вручную email внешнего гостя.
    return { guestEmail: value.trim().toLowerCase() };
  });
}

interface MeetingFormValues {
  title: string;
  description?: string | undefined;
  startLocal: string;
  endLocal: string;
  timezone?: string | undefined;
  location?: string | undefined;
  participants?: string[] | undefined;
  dealId?: string | undefined;
  companyId?: string | undefined;
  contactId?: string | undefined;
}

interface MeetingFormModalProps {
  open: boolean;
  onClose: () => void;
  meeting?: MeetingItem | null;
  lockedDealId?: string;
  lockedContactId?: string;
  lockedCompanyId?: string;
  /** Встреча, назначенная с карточки задачи или документа, привязана к ним. */
  lockedTaskId?: string;
  lockedDocumentId?: string;
  /** Встреча с карточки проекта привязана к проекту. */
  lockedProjectId?: string;
  /** Планировщик: предзаполнить дату начала (ISO) выбранного дня. */
  initialStartsAt?: string;
}

export function MeetingFormModal({
  open,
  onClose,
  meeting,
  lockedDealId,
  lockedContactId,
  lockedCompanyId,
  lockedTaskId,
  lockedDocumentId,
  lockedProjectId,
  initialStartsAt,
}: MeetingFormModalProps): ReactElement {
  useI18nSubscription();
  const locale = i18n.locale;
  const [form] = Form.useForm<MeetingFormValues>();
  const isEdit = Boolean(meeting);

  const membersQuery = useMembers();
  const contactsQuery = useContacts();
  // Каскад «компания → контакт → сделка» — тот же, что в форме задачи.
  const cascade = useEntityLinkCascade(form);
  // Следим за началом: от него зависит минимум в поле окончания.
  const startLocal = Form.useWatch('startLocal', form);

  const createMut = useCreateMeetingMutation();
  const updateMut = useUpdateMeetingMutation();
  const pending = createMut.isPending || updateMut.isPending;
  const error = (createMut.error ?? updateMut.error) as Error | null;

  const participantOptions = useMemo(
    () => [
      {
        label: i18n.t('crm.features.features.meetings.components.meetingformmodal.сотрудники'),
        options: (membersQuery.data ?? []).map((m) => ({
          value: `${MEMBER_PREFIX}${m.id}`,
          label: m.name || m.email,
        })),
      },
      {
        label: i18n.t('crm.features.features.meetings.components.meetingformmodal.контакты'),
        options: (contactsQuery.data ?? []).map((c) => ({
          value: `${CONTACT_PREFIX}${c.id}`,
          label: c.email ? `${c.fullName} · ${c.email}` : c.fullName,
        })),
      },
    ],
    [membersQuery.data, contactsQuery.data, locale],
  );

  useEffect(() => {
    if (!open) return;
    createMut.reset();
    updateMut.reset();
    if (meeting) {
      form.setFieldsValue({
        title: meeting.title,
        description: meeting.description ?? '',
        startLocal: isoToLocalInput(meeting.startsAt),
        endLocal: isoToLocalInput(meeting.endsAt),
        timezone: meeting.timezone,
        location: meeting.location ?? '',
        participants: participantsToValues(meeting),
        dealId: meeting.deal?.id ?? undefined,
        companyId: meeting.company?.id ?? undefined,
        contactId: meeting.contact?.id ?? undefined,
      });
    } else {
      // Quick-create: the planner hands an ISO start preset. End defaults to
      // start + 1h (typical meeting length) so submitting no longer bounces
      // on the empty-end validation error.
      const startLocalValue = initialStartsAt ? isoToLocalInput(initialStartsAt) : '';
      const endLocalValue = initialStartsAt
        ? isoToLocalInput(new Date(new Date(initialStartsAt).getTime() + 60 * 60 * 1000))
        : '';
      form.setFieldsValue({
        title: '',
        description: '',
        startLocal: startLocalValue,
        endLocal: endLocalValue,
        timezone: 'Europe/Moscow',
        location: '',
        participants: [],
        dealId: lockedDealId ?? undefined,
        companyId: lockedCompanyId ?? undefined,
        contactId: lockedContactId ?? undefined,
      });
    }
  }, [open, meeting, lockedDealId, lockedCompanyId, lockedContactId, initialStartsAt, form]);

  const handleSubmit = (values: MeetingFormValues): void => {
    const startsAt = localInputToIso(values.startLocal);
    const endsAt = localInputToIso(values.endLocal);
    if (!startsAt || !endsAt) return;

    const participants = valuesToParticipants(values.participants ?? []);

    if (isEdit && meeting) {
      updateMut.mutate(
        {
          id: meeting.id,
          input: {
            title: values.title,
            description: values.description?.trim() ? values.description : null,
            startsAt,
            endsAt,
            ...(values.timezone ? { timezone: values.timezone } : {}),
            location: values.location?.trim() ? values.location : null,
            dealId: lockedDealId ?? values.dealId ?? null,
            companyId: lockedCompanyId ?? values.companyId ?? null,
            contactId: lockedContactId ?? values.contactId ?? null,
            ...(lockedTaskId ? { taskId: lockedTaskId } : {}),
            ...(lockedDocumentId ? { documentId: lockedDocumentId } : {}),
            ...(lockedProjectId ? { projectId: lockedProjectId } : {}),
            participants,
            expectedUpdatedAt: meeting.updatedAt,
          },
        },
        { onSuccess: () => onClose() },
      );
      return;
    }

    const dealId = lockedDealId ?? values.dealId;
    const companyId = lockedCompanyId ?? values.companyId;
    const contactId = lockedContactId ?? values.contactId;
    const input: CreateMeetingInput = {
      title: values.title,
      startsAt,
      endsAt,
      participants,
      ...(values.description?.trim() ? { description: values.description } : {}),
      ...(values.timezone ? { timezone: values.timezone } : {}),
      ...(values.location?.trim() ? { location: values.location } : {}),
      ...(dealId ? { dealId } : {}),
      ...(companyId ? { companyId } : {}),
      ...(contactId ? { contactId } : {}),
      ...(lockedTaskId ? { taskId: lockedTaskId } : {}),
      ...(lockedDocumentId ? { documentId: lockedDocumentId } : {}),
      ...(lockedProjectId ? { projectId: lockedProjectId } : {}),
    };
    createMut.mutate(input, { onSuccess: () => onClose() });
  };

  const minLocal = nowLocalInput();

  return (
    <Modal
      title={
        isEdit
          ? i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.редактировать_встречу',
            )
          : i18n.t('crm.features.features.meetings.components.meetingformmodal.новая_встреча')
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.meetings.components.meetingformmodal.сохранить')
          : i18n.t('crm.features.features.meetings.components.meetingformmodal.назначить')
      }
      cancelText={i18n.t('crm.features.features.meetings.components.meetingformmodal.отмена')}
      confirmLoading={pending}
      width={580}
      destroyOnHidden
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={i18n.t(
            'crm.features.features.meetings.components.meetingformmodal.не_удалось_сохранить_встречу',
          )}
          description={error.message}
        />
      ) : null}

      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={pending}>
        <Form.Item
          name="title"
          label={i18n.t('crm.features.features.meetings.components.meetingformmodal.тема')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: i18n.t(
                'crm.features.features.meetings.components.meetingformmodal.введите_тему_встречи',
              ),
            },
          ]}
        >
          <Input
            placeholder={i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.о_чем_встреча',
            )}
            maxLength={200}
            autoFocus
          />
        </Form.Item>

        <div className="flex gap-3">
          <Form.Item
            name="startLocal"
            label={i18n.t('crm.features.features.meetings.components.meetingformmodal.начало')}
            className="flex-1"
            rules={[
              {
                required: true,
                message: i18n.t(
                  'crm.features.features.meetings.components.meetingformmodal.укажите_начало',
                ),
              },
            ]}
          >
            <input
              type="datetime-local"
              min={isEdit ? undefined : minLocal}
              className="h-8 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition hover:border-amber-400 focus:border-amber-500"
            />
          </Form.Item>
          <Form.Item
            name="endLocal"
            label={i18n.t('crm.features.features.meetings.components.meetingformmodal.окончание')}
            className="flex-1"
            dependencies={['startLocal']}
            rules={[
              {
                required: true,
                message: i18n.t(
                  'crm.features.features.meetings.components.meetingformmodal.укажите_окончание',
                ),
              },
              ({ getFieldValue }) => ({
                validator: (_rule, value: string | undefined) => {
                  const start = getFieldValue('startLocal') as string | undefined;
                  if (!value || !start) return Promise.resolve();
                  return new Date(value).getTime() > new Date(start).getTime()
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          i18n.t(
                            'crm.features.features.meetings.components.meetingformmodal.окончание_должно_быть_позже_начала',
                          ),
                        ),
                      );
                },
              }),
            ]}
          >
            {/* Минимум — выбранное начало: встреча не может кончиться раньше,
                чем началась, и календарь сразу открывается на нужной дате. */}
            <input
              type="datetime-local"
              min={startLocal || (isEdit ? undefined : minLocal)}
              className="h-8 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition hover:border-amber-400 focus:border-amber-500"
            />
          </Form.Item>
        </div>

        <div className="flex gap-3">
          <Form.Item
            name="timezone"
            label={i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.часовой_пояс',
            )}
            className="flex-1"
          >
            <Select
              showSearch
              options={[
                { value: 'Europe/Moscow', label: 'Europe/Moscow' },
                { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad' },
                { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg' },
                { value: 'Asia/Novosibirsk', label: 'Asia/Novosibirsk' },
                { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok' },
                { value: 'UTC', label: 'UTC' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="location"
            label={i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.место_ссылка',
            )}
            className="flex-1"
          >
            <Input
              placeholder={i18n.t(
                'crm.features.features.meetings.components.meetingformmodal.адрес_или_ссылка_на_созвон',
              )}
              maxLength={300}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="participants"
          label={i18n.t('crm.features.features.meetings.components.meetingformmodal.участники')}
          extra={i18n.t(
            'crm.features.features.meetings.components.meetingformmodal.сотрудники_и_контакты_из_списка_внешнего_г',
          )}
        >
          <Select
            mode="tags"
            allowClear
            optionFilterProp="label"
            placeholder={i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.кого_приглашаем',
            )}
            loading={membersQuery.isLoading || contactsQuery.isLoading}
            options={participantOptions}
            tokenSeparators={[',', ' ']}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={i18n.t('crm.features.features.meetings.components.meetingformmodal.описание')}
        >
          <Input.TextArea
            rows={2}
            maxLength={10_000}
            placeholder={i18n.t(
              'crm.features.features.meetings.components.meetingformmodal.повестка_необязательно',
            )}
          />
        </Form.Item>

        <EntityLinkFields
          cascade={cascade}
          {...(lockedDealId ? { lockedDealId } : {})}
          {...(lockedContactId ? { lockedContactId } : {})}
          {...(lockedCompanyId ? { lockedCompanyId } : {})}
        />
      </Form>
    </Modal>
  );
}
